/**
 * The one mapper from the BFF's assessment (`AssessmentResponse`, with a `source` on every number) to what the ported
 * Growth Score components read: names from the labels, a dimension's checks, the strips with their ranks, the "how we
 * measured it" rows, every check's opportunity (the BFF's, else a text with no figure of its own) and the Search & AI blocks. Pure.
 *
 * `source` ('measured' / 'example') is read and dropped here: nothing is labelled "example" for now (plan P4-3, TD-4.10).
 */
import type {
  AssessmentOpportunity,
  AssessmentResponse,
  BrandGuideResponse,
  CheckKey,
  CohortCompetitor,
  DimensionKey,
  Hypothesis,
} from '@/contracts/onboarding-session'
import { DIMENSION_CHECK_KEYS } from '@/contracts/onboarding-session'
import { prospectLogoUrl } from '@/features/onboarding-chat/state/researchSteps'
import { brandLabel } from '@/features/onboarding-chat/state/buildTranscript'
import type { OnboardingSessionState } from '@/features/onboarding-chat/state/session'
import { toBrandProfileInput } from '@/features/onboarding-chat/quiz/profile-input'
import { normalizeDomain } from '@/features/onboarding-chat/quiz/website'

import { opportunityFor } from './growth-score-content'
import { CHECK_LABELS, DIMENSION_LABELS } from './growth-score-labels'
import { AD_METRICS, answerFacts, copyContext, stripStats, type MetricStats } from './growth-score-model'
import type { AdMetricKey, BrandProfileInput, Check, Dimension, KvRow, Opportunity, SearchBlock } from './growth-score.types'
import type { CopyContext } from './growth-score-content'
import { SEARCH_BLOCK_CONTENT } from './search-content'

export interface StripVm {
  key: AdMetricKey
  label: string
  sub: string
  unit: string
  you: number
  stats: MetricStats
}

/** A "How we measured it" row: the check's name over what it measures, then the BFF's three display strings. */
export interface MeasuredRowVm {
  check: CheckKey
  name: string
  sub?: string
  you: string
  cohortMedian: string
  rank: string
}

export interface OpportunityVm {
  opportunity: Opportunity
  /** Points the check would gain (the BFF's); null for the fallback text. */
  pointsGained: number | null
}

export interface WorstCheck extends Check {
  dimension: DimensionKey
}

export interface GrowthScoreVm {
  site: string
  brand: string
  /** The brand's logo from its own site (the brand guide), http(s) only; null draws the initial. */
  logoUrl: string | null
  guide: BrandGuideResponse | null
  /** What the person confirmed in the chat (browser only); null when they did not. */
  confirmedProfile: OnboardingSessionState['brandProfile']
  /** The quiz answers, typed. */
  profile: BrandProfileInput
  dimensions: Dimension[]
  overall: number
  worst: WorstCheck
  strips: StripVm[]
  measuredFor: (dimension: DimensionKey) => MeasuredRowVm[]
  opportunityFor: (check: string) => OpportunityVm
  competitors: CohortCompetitor[]
  hypotheses: Hypothesis[]
  searchBlocks: SearchBlock[]
  copy: CopyContext
  /** The person skipped the competitors step (confirmed none). An empty `competitors` without this just means the list is not there. */
  competitorsSkipped: boolean
}

export interface GrowthScoreInputs {
  assessment: AssessmentResponse
  session: Pick<OnboardingSessionState, 'url' | 'answers' | 'brandProfile'>
  guide: BrandGuideResponse | null
  /** The competitors step was skipped; false when it was not, or when that is not known. */
  competitorsSkipped?: boolean
}

function fromBff(o: AssessmentOpportunity): OpportunityVm {
  return {
    pointsGained: o.pointsGained,
    opportunity: {
      summary: o.copy.title,
      whatWeSee: o.copy.problem,
      // The BFF's `action` is the lever; it has no body or link of its own.
      lever: { title: o.copy.action, body: '', alreadyDoing: o.cohortEvidence.map((b) => b.name), link: '' },
    },
  }
}

export function toGrowthScoreVm({ assessment, session, guide, competitorsSkipped = false }: GrowthScoreInputs): GrowthScoreVm {
  const profile = toBrandProfileInput(session.answers)
  const brand = brandLabel(null, session.url, guide?.name.value)
  const site = session.url ? normalizeDomain(session.url) : ''

  const dimensions: Dimension[] = assessment.dimensions.map((d) => ({
    key: d.key,
    name: DIMENSION_LABELS[d.key].name,
    short: DIMENSION_LABELS[d.key].short,
    weight: d.weight,
    score: d.score,
    gap: d.gap,
    checks: d.checks.map((c) => ({
      key: c.key,
      name: CHECK_LABELS[c.key].name,
      score: c.score,
      ...(c.label !== undefined ? { label: c.label } : {}),
    })),
  }))

  const strips: StripVm[] = AD_METRICS.flatMap((m) => {
    const strip = assessment.cohort.strip.find((s) => s.metric === m.key)
    return strip ? [{ key: m.key, label: m.label, sub: m.sub, unit: m.unit, you: strip.you, stats: stripStats(strip) }] : []
  })
  // The fallback texts quote a figure only from a strip the BFF sent; there is no stand-in median.
  const stripOf = (k: AdMetricKey) => {
    const s = strips.find((x) => x.key === k)
    return s ? { you: s.you, median: s.stats.median } : undefined
  }
  const copy = copyContext(brand, answerFacts(profile), stripOf)

  const bff = new Map<string, AssessmentOpportunity>(assessment.opportunities.map((o) => [o.check, o]))
  const opportunity = (check: string): OpportunityVm => {
    const own = bff.get(check)
    if (own) return fromBff(own)
    const text = opportunityFor(check, copy)
    return { pointsGained: null, opportunity: text ?? { summary: '', whatWeSee: '' } }
  }

  const worstDimension = dimensions.find((d) => d.key === assessment.worst.dimension)
  const worstCheck = worstDimension?.checks.find((c) => c.key === assessment.worst.key)
  const worst: WorstCheck = {
    key: assessment.worst.key,
    name: worstCheck?.name ?? CHECK_LABELS[assessment.worst.key].name,
    score: assessment.worst.score,
    dimension: assessment.worst.dimension,
  }

  const searchBlocks: SearchBlock[] = assessment.searchBlocks.map((b) => ({
    key: b.key,
    score: b.score,
    name: CHECK_LABELS[b.key].name,
    gap: SEARCH_BLOCK_CONTENT[b.key].gap,
    rows: assessment.measured.filter((r) => r.check === b.key).map((r): KvRow => [CHECK_LABELS[r.check].name, r.you, r.cohortMedian, r.rank]),
  }))

  return {
    site,
    brand,
    logoUrl: prospectLogoUrl(guide),
    guide,
    confirmedProfile: session.brandProfile,
    profile,
    dimensions,
    overall: assessment.overall.score,
    worst,
    strips,
    measuredFor: (dimension) =>
      assessment.measured
        .filter((r) => (DIMENSION_CHECK_KEYS[dimension] as readonly string[]).includes(r.check))
        .map((r) => ({ check: r.check, ...CHECK_LABELS[r.check], you: r.you, cohortMedian: r.cohortMedian, rank: r.rank })),
    opportunityFor: opportunity,
    competitors: assessment.cohort.competitors,
    hypotheses: assessment.hypotheses,
    searchBlocks,
    copy,
    competitorsSkipped,
  }
}
